import { Icon } from '@mdvip/shared/components/blocks/Icon';
import {
  ResponsiveModal,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from '@mdvip/shared/components/blocks/ResponsiveModal';
import { Alert, AlertDescription, AlertTitle } from '@mdvip/shared/components/ui/alert';
import { Button } from '@mdvip/shared/components/ui/button';
import { useCurrency } from '@mdvip/shared/hooks/useCurrency';
import { useFormatDate } from '@mdvip/shared/hooks/useFormatDate';
import { MDVIP_COMPANY_INFO } from '@mdvip/shared/lib/mdvip-company-info';
import { useEffect, useState } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMemberAutoPaySummary } from '@/hooks/payments/autoPay/useMemberAutoPaySummary';
import { useMaximumPaymentAmount } from '@/hooks/payments/oneTimePayment/useMaximumPaymentAmount';
import { useOneTimePaymentForm } from '@/hooks/payments/oneTimePayment/useOneTimePaymentForm';
import { useScheduleOneTimePayment } from '@/hooks/payments/oneTimePayment/useScheduleOneTimePayment';
import type { SavedPaymentAccount } from '@/hooks/payments/paymentApi';
import { useSavedPaymentAccounts } from '@/hooks/payments/useSavedPaymentAccounts';
import { earliestScheduleDate, lastChargeableDate, parseAmount } from '@/lib/payments/oneTimePayment.format';
import { MIN_PAYMENT_AMOUNT, type OneTimePaymentFormValues } from '@/lib/payments/oneTimePayment.schema';
import { type PaymentAccountItem, PickerPaymentAccount } from '../PickerPaymentAccount';
import { AmountField, ScheduleDateField, useTranslateError } from './fields';

/**
 * Fallback cap used until `maximumPaymentAmount` resolves, and if it fails outright. Deliberately
 * generous: the API is the real gate (`AMOUNT_EXCEEDS_MAXIMUM`), so a metadata outage should not
 * block a member from submitting a legitimate amount.
 */
const FALLBACK_MAX_AMOUNT = 50000;

interface OneTimePaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opens the add-payment-account flow from the picker or the empty state. */
  onAddAccount?: () => void;
}

/**
 * Saved account → picker item. Cards read "VISA ···0009 · Exp 05/2029", banks "Chase ···123456".
 *
 * `expPrefix` is the neutral "Exp" label, not the expired-card badge: the expiry line states when a
 * card runs out, whether or not it already has. Whether the card is expired is carried by the
 * `expired` flag, which the picker renders as its own badge.
 */
function toItem(account: SavedPaymentAccount, expPrefix: string): PaymentAccountItem {
  const expiry =
    account.expirationMonth !== null && account.expirationYear !== null
      ? ` · ${expPrefix} ${String(account.expirationMonth).padStart(2, '0')}/${account.expirationYear}`
      : '';
  return {
    id: account.id,
    label: `${account.descriptor} ···${account.suffix}${expiry}`,
    isDefault: account.isDefault,
    expired: account.isExpired,
    bank: account.kind === 'bank',
  };
}

/**
 * OneTimePaymentModal — schedule a single payment toward the membership balance.
 *
 * Folds three steps into one modal, as the design does: an AutoPay interstitial that gates entry
 * when the member is enrolled, the form, and a success screen. Schedule stays enabled so amount,
 * date and account errors surface on click; only the in-flight state disables it. A failure keeps
 * the modal open with the form intact, with the error raised as a toast by the mutation hook.
 *
 * Unlike AutoPay enrollment, expired cards are listed rather than filtered out, matching this
 * modal's design: selecting one is a validation error that tells the member to pick another.
 *
 * State lives for as long as the component is mounted, deliberately. The caller remounts it (via a
 * `key`) when a new payment flow starts, and keeps it mounted while the member detours to add an
 * account, so returning lands them back where they were, with the interstitial already dismissed and
 * anything they had typed intact.
 */
export function OneTimePaymentModal({ open, onOpenChange, onAddAccount }: OneTimePaymentModalProps) {
  const { t } = useTranslation('members');
  const translateError = useTranslateError();
  const { formatCurrency } = useCurrency();
  const { formatDateOnly } = useFormatDate();

  const { data: summary } = useMemberAutoPaySummary();
  const { data: accounts = [], isLoading: isLoadingAccounts } = useSavedPaymentAccounts();
  const { data: maximum } = useMaximumPaymentAmount();
  const { schedule, isPending } = useScheduleOneTimePayment();

  const autoPayEnrolled = summary?.autoPayEnrolled ?? false;
  const maxAmount = maximum?.amount ?? FALLBACK_MAX_AMOUNT;
  const expiredAccountIds = accounts.filter((account) => account.isExpired).map((account) => account.id);
  // Cards die at the end of their expiration month, so scheduling past that would never process.
  // Bank accounts carry no expiration and are simply absent from the lookup.
  const lastChargeableDates = Object.fromEntries(
    accounts
      .filter((account) => account.expirationMonth !== null && account.expirationYear !== null)
      .map((account) => [
        account.id,
        lastChargeableDate(account.expirationMonth as number, account.expirationYear as number),
      ]),
  );
  const items = accounts.map((account) => toItem(account, t('payments.account.expPrefix')));
  const hasAccounts = items.length > 0;
  // A just-added account arrives here as the member's new default via the saved-methods cache
  // invalidation, so preselecting the default covers the add-an-account-mid-flow case too.
  const defaultAccountId = (accounts.find((account) => account.isDefault) ?? accounts[0])?.id || '';

  const [dismissedInterstitial, setDismissedInterstitial] = useState(false);
  const [scheduled, setScheduled] = useState<OneTimePaymentFormValues | null>(null);
  const form = useOneTimePaymentForm({
    maxAmount,
    expiredAccountIds,
    lastChargeableDates,
    defaultPaymentProfileId: defaultAccountId,
  });

  /**
   * Derived, not stored. Both `autoPayEnrolled` and the account list start empty and resolve
   * asynchronously, so anything computed once at open-time would be computed from data that had not
   * arrived: an enrolled member on a cold cache would be shown the form instead of the gate.
   * Deriving means the interstitial appears as soon as the summary lands, and Continue is the only
   * thing that dismisses it.
   */
  const step: 'interstitial' | 'form' | 'success' = scheduled
    ? 'success'
    : autoPayEnrolled && !dismissedInterstitial
      ? 'interstitial'
      : 'form';

  // Applies the default once the accounts resolve, touching only the account field so a
  // part-completed form survives. Skipped once the field holds a value, so it never overrides a
  // selection the member made while the query was still in flight.
  useEffect(() => {
    if (!open || !defaultAccountId) return;
    if (form.getValues('paymentProfileId') === '') {
      form.setValue('paymentProfileId', defaultAccountId);
    }
  }, [open, defaultAccountId, form]);

  const handleSubmit = form.handleSubmit(async (values) => {
    const amount = parseAmount(values.amount);
    if (amount === null) return;
    try {
      await schedule({
        amount,
        paymentProfileId: values.paymentProfileId,
        scheduleDate: values.scheduleDate,
      });
      // Recording what was scheduled is what advances the modal to its success step.
      setScheduled(values);
    } catch {
      // The hook raises the toast; keep the modal open so the member can adjust and retry.
    }
  });

  if (step === 'interstitial') {
    return (
      <ResponsiveModal open={open} onOpenChange={onOpenChange} className="sm:max-w-sm">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle>{t('payments.oneTimePayment.interstitial.title')}</ResponsiveModalTitle>
        </ResponsiveModalHeader>
        <ResponsiveModalBody>
          <Alert variant="warning">
            <Icon name="alertTriangle" weight="solid" />
            <AlertTitle>{t('payments.oneTimePayment.interstitial.alertTitle')}</AlertTitle>
            <AlertDescription>
              <p>
                {t('payments.oneTimePayment.interstitial.alertBody', {
                  phone: MDVIP_COMPANY_INFO.memberSupportPhone,
                })}
              </p>
            </AlertDescription>
          </Alert>
        </ResponsiveModalBody>
        <ResponsiveModalFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('payments.oneTimePayment.interstitial.cancel')}
          </Button>
          <Button type="button" onClick={() => setDismissedInterstitial(true)}>
            {t('payments.oneTimePayment.interstitial.continue')}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModal>
    );
  }

  if (step === 'success') {
    const amount = parseAmount(scheduled?.amount ?? '');
    const method = items.find((item) => item.id === scheduled?.paymentProfileId)?.label;
    return (
      // Same width as the form step, deliberately: the design keeps one size so the dialog does not
      // resize as it advances.
      <ResponsiveModal open={open} onOpenChange={onOpenChange} className="sm:max-w-lg">
        <ResponsiveModalBody>
          <div className="flex min-h-full flex-col items-center justify-center text-center sm:min-h-0 sm:py-2">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-success text-success-foreground">
              <Icon name="circleCheck" weight="solid" className="text-[28px]" />
            </div>
            <div className="text-xl font-light text-foreground">{t('payments.oneTimePayment.success.eyebrow')}</div>
            <ResponsiveModalTitle className="mb-4 text-3xl font-bold leading-tight text-foreground">
              {t('payments.oneTimePayment.success.title')}
            </ResponsiveModalTitle>
            <div className="w-full rounded-xl border bg-muted text-left">
              <div className="flex justify-between border-b px-4 py-2.5 text-sm">
                <span className="text-muted-foreground">{t('payments.oneTimePayment.success.amount')}</span>
                <span className="font-semibold tabular-nums">{amount === null ? '—' : formatCurrency(amount)}</span>
              </div>
              <div className="flex justify-between border-b px-4 py-2.5 text-sm">
                <span className="text-muted-foreground">{t('payments.oneTimePayment.success.date')}</span>
                <span className="font-semibold">
                  {scheduled?.scheduleDate ? formatDateOnly(scheduled.scheduleDate) : '—'}
                </span>
              </div>
              <div className="flex justify-between px-4 py-2.5 text-sm">
                <span className="text-muted-foreground">{t('payments.oneTimePayment.success.method')}</span>
                <span className="ml-4 text-right font-semibold">{method ?? '—'}</span>
              </div>
            </div>
            <p className="mt-3 text-pretty text-xs text-muted-foreground">
              {t('payments.oneTimePayment.success.confirmationNote')}
            </p>
          </div>
        </ResponsiveModalBody>
        <ResponsiveModalFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            {t('payments.oneTimePayment.success.backToPaymentCenter')}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModal>
    );
  }

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange} className="sm:max-w-lg">
      <ResponsiveModalHeader>
        <ResponsiveModalTitle>{t('payments.oneTimePayment.title')}</ResponsiveModalTitle>
        <p className="mt-2 text-sm text-muted-foreground">{t('payments.oneTimePayment.description')}</p>
      </ResponsiveModalHeader>

      <ResponsiveModalBody>
        <FormProvider {...form}>
          <div className="space-y-4">
            <AmountField
              label={t('payments.oneTimePayment.amountLabel')}
              helper={t('payments.oneTimePayment.amountHelper', { max: formatCurrency(maxAmount) })}
              rangeValues={{ min: formatCurrency(MIN_PAYMENT_AMOUNT), max: formatCurrency(maxAmount) }}
              maxAmount={maxAmount}
            />

            {isLoadingAccounts ? (
              // Same footprint as the picker, so resolving the list doesn't shift the layout.
              <div
                role="status"
                aria-busy="true"
                aria-label={t('payments.oneTimePayment.loadingAccounts')}
                className="h-12 w-full animate-pulse rounded-md border border-input bg-muted"
              />
            ) : hasAccounts ? (
              <Controller
                control={form.control}
                name="paymentProfileId"
                render={({ field, fieldState }) => (
                  <div>
                    <PickerPaymentAccount
                      items={items}
                      value={field.value}
                      onChange={field.onChange}
                      label={t('payments.oneTimePayment.accountLabel')}
                      error={fieldState.invalid}
                      onAddNew={onAddAccount}
                      addNewLabel={t('payments.oneTimePayment.addAccount')}
                      defaultLabel={t('payments.oneTimePayment.defaultBadge')}
                      expiredLabel={t('payments.oneTimePayment.expiredBadge')}
                    />
                    {fieldState.error?.message && (
                      <p className="mt-1.5 flex items-start gap-1.5 text-xs text-destructive-foreground">
                        <Icon name="alertCircle" weight="solid" size="xs" className="mt-px shrink-0" />
                        {translateError(fieldState.error.message)}
                      </p>
                    )}
                  </div>
                )}
              />
            ) : (
              <div className="rounded-lg border border-dashed p-5 text-center">
                <div className="mx-auto mb-2.5 flex h-10 w-10 items-center justify-center rounded-full border bg-muted text-foreground">
                  <Icon name="creditCard" />
                </div>
                <div className="text-sm font-semibold text-foreground">
                  {t('payments.oneTimePayment.noAccountsTitle')}
                </div>
                <p className="mb-3 mt-1 text-xs text-muted-foreground">{t('payments.oneTimePayment.noAccountsBody')}</p>
                {onAddAccount && (
                  <Button type="button" size="sm" className="mx-auto" onClick={onAddAccount}>
                    <Icon name="plus" weight="solid" size="sm" /> {t('payments.oneTimePayment.addAccountButton')}
                  </Button>
                )}
              </div>
            )}

            <ScheduleDateField
              label={t('payments.oneTimePayment.dateLabel')}
              helper={t('payments.oneTimePayment.dateHelper')}
              min={earliestScheduleDate()}
            />
          </div>
        </FormProvider>
      </ResponsiveModalBody>

      <ResponsiveModalFooter
        note={
          <>
            <Icon name="lock" weight="solid" size="xs" /> {t('payments.oneTimePayment.secureNote')}
          </>
        }
      >
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t('payments.oneTimePayment.cancel')}
        </Button>
        <Button type="button" onClick={handleSubmit} disabled={isPending || !hasAccounts}>
          {isPending ? (
            <>
              <Icon name="spinner" weight="solid" size="sm" className="animate-spin" />
              {t('payments.oneTimePayment.submitting')}
            </>
          ) : (
            <>
              <Icon name="calendar" size="sm" /> {t('payments.oneTimePayment.submit')}
            </>
          )}
        </Button>
      </ResponsiveModalFooter>
    </ResponsiveModal>
  );
}
